import { theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { IoArrowForward } from 'react-icons/io5'

import { classNames } from '@/Ship/Helpers/classNames'
import { dashboardManager } from '@/Ship/Stores/DashboardManager'

import type { IOverviewSourceFunnel } from '@/Ship/Contracts/IHomeOverview'

import { PERIOD_LABELS, Panel, formatInt, formatMoney, formatShare, shownOf, useChartPalette, useChartColors } from './shared'
import styles from './Overview.module.css'

/** Скільки рядків рейтингу логістів показувати. */
export const LEADERBOARD_ROWS = 7

const periodCaption = (): string =>
    PERIOD_LABELS[dashboardManager.overview?.period.preset ?? dashboardManager.period].toLowerCase()

/**
 * «Рейтинг логістів» — прибуток виконаних перевезень за період, від
 * більшого; під іменем — кількість і маржа. Лише в того, хто бачить усіх
 * логістів (`logisticians_all`): бек інакше віддає `null`, і блоку немає.
 * Обраний у фільтрі співробітник — виділений.
 */
export const LeaderboardCard = observer(() => {
    const { token } = theme.useToken()
    const { overview, isOverviewLoading, selectedUuid } = dashboardManager
    const rows = overview?.leaderboard ?? []
    const shown = rows.slice(0, LEADERBOARD_ROWS)
    const max = Math.max(1, ...shown.map((row) => row.profit))

    return (
        <Panel
            block="overview-leaderboard"
            title="Рейтинг логістів"
            subtitle={`Прибуток за ${periodCaption()}, без програних`}
            extra={overview ? shownOf(shown.length, rows.length, 'логістів') : null}
            loading={isOverviewLoading && !overview}
            empty={
                overview && rows.length === 0 ? 'За період немає виконаних перевезень з відповідальним логістом' : false
            }
            skeletonRows={8}
        >
            <div className={classNames(styles['bars'])}>
                {shown.map((row, index) => (
                    <div
                        key={row.user.uuid}
                        className={classNames(
                            styles['barRow'],
                            row.user.uuid === selectedUuid ? styles['highlight'] : undefined
                        )}
                        data-leader={row.user.uuid}
                    >
                        <span className={classNames(styles['leader'])}>
                            <span className={classNames(styles['rank'], index < 3 ? styles['rankTop'] : undefined)}>
                                {index + 1}
                            </span>
                            <span className={classNames(styles['barLabel'])} title={row.user.fullName ?? ''}>
                                {row.user.fullName ?? '—'}
                                {row.user.isActive ? null : (
                                    <span className={classNames(styles['barMuted'])}>деактивований</span>
                                )}
                                <span className={classNames(styles['barMuted'])}>
                                    {formatInt(row.applications)} перев. · маржа {formatShare(row.margin)}
                                </span>
                            </span>
                        </span>
                        <span className={classNames(styles['barValue'])}>{formatMoney(row.profit)}</span>
                        <div className={classNames(styles['track'])}>
                            <div
                                className={classNames(styles['fill'])}
                                style={{
                                    width: `${(Math.max(0, row.profit) / max) * 100}%`,
                                    background: token.colorSuccess,
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </Panel>
    )
})

/**
 * «Топ маршрутів» — місто першого завантаження → місто останнього
 * розвантаження, за кількістю перевезень за період. Перелік обрізаний —
 * праворуч сказано, скільки маршрутів усього.
 */
export const RoutesCard = observer(() => {
    const { token } = theme.useToken()
    const chart = useChartColors()
    const { overview, isOverviewLoading } = dashboardManager
    const routes = overview?.routes
    const items = routes?.items ?? []
    const max = Math.max(1, ...items.map((row) => row.applications))

    return (
        <Panel
            block="overview-routes"
            title="Топ маршрутів"
            subtitle={`Перевезення за ${periodCaption()}, за датою завантаження`}
            extra={routes ? shownOf(items.length, routes.routes, 'маршрутів') : null}
            loading={isOverviewLoading && !overview}
            empty={overview && items.length === 0 ? 'За період немає перевезень з адресами' : false}
            skeletonRows={8}
        >
            <div className={classNames(styles['bars'])}>
                {items.map((row, index) => (
                    <div
                        key={`${row.from}-${row.to}`}
                        className={classNames(styles['barRow'])}
                        data-route={`${row.from}→${row.to}`}
                    >
                        <span className={classNames(styles['leader'])}>
                            <span className={classNames(styles['rank'], index < 3 ? styles['rankTop'] : undefined)}>
                                {index + 1}
                            </span>
                            <span className={classNames(styles['barLabel'])} title={`${row.from} → ${row.to}`}>
                                {row.from}{' '}
                                <IoArrowForward style={{ verticalAlign: -2, color: token.colorTextTertiary }} />{' '}
                                {row.to}
                            </span>
                        </span>
                        <span className={classNames(styles['barValue'])}>
                            {formatInt(row.applications)}
                            <span className={classNames(styles['barMuted'])}>{formatMoney(row.profit)}</span>
                        </span>
                        <div className={classNames(styles['track'])}>
                            <div
                                className={classNames(styles['fill'])}
                                style={{ width: `${(row.applications / max) * 100}%`, background: chart.third }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </Panel>
    )
})

/** Частини смуги джерела — у порядку руху: стали заявкою, з ціною, ще без ціни, програні. */
const SOURCE_PARTS = [
    { key: 'won', title: 'Стали заявкою' },
    { key: 'priced', title: 'З ціною, чекають клієнта' },
    { key: 'new', title: 'Без ціни' },
    { key: 'lost', title: 'Програні' },
] as const

/** Конверсія — виграні серед вирішених (виграні + програні); без вирішених — «—». */
export const conversion = (funnel: IOverviewSourceFunnel): number | null =>
    funnel.won + funnel.lost > 0 ? Math.round((funnel.won / (funnel.won + funnel.lost)) * 1000) / 10 : null

const SourceRow = ({
    title,
    funnel,
    colors,
}: {
    title: string
    funnel: IOverviewSourceFunnel
    colors: Record<(typeof SOURCE_PARTS)[number]['key'], string>
}) => (
    <div className={classNames(styles['source'])} data-source={title}>
        <div className={classNames(styles['sourceHead'])}>
            <span className={classNames(styles['sourceTitle'])}>
                {title}
                <span className={classNames(styles['barMuted'])}>надійшло {formatInt(funnel.total)}</span>
            </span>
            <span title="Конверсія: стали заявкою серед вирішених (заявка або програш)">
                <span className={classNames(styles['barMuted'])} style={{ marginRight: 6 }}>
                    конверсія
                </span>
                <span className={classNames(styles['sourceRate'])}>{formatShare(conversion(funnel))}</span>
            </span>
        </div>
        <div className={classNames(styles['stack'])}>
            {SOURCE_PARTS.map((part) =>
                funnel[part.key] > 0 ? (
                    <span
                        key={part.key}
                        title={`${part.title}: ${funnel[part.key]}`}
                        style={{
                            width: `${(funnel[part.key] / Math.max(1, funnel.total)) * 100}%`,
                            background: colors[part.key],
                        }}
                    />
                ) : null
            )}
        </div>
        <div className={classNames(styles['legend'])}>
            {SOURCE_PARTS.map((part) => (
                <span key={part.key} className={classNames(styles['legendItem'])}>
                    <span className={classNames(styles['swatch'])} style={{ background: colors[part.key] }} />
                    {part.title} <b>{formatInt(funnel[part.key])}</b>
                </span>
            ))}
        </div>
    </div>
)

/**
 * «Запити й тендери → заявки» — що сталося з запитами й тендерами, що
 * надійшли за період: стали заявкою, чекають клієнта з ціною, ще без ціни,
 * програні; конверсія — серед вирішених. Знизу — скільки цінових пропозицій
 * дано за той самий період.
 */
export const SourcesCard = observer(() => {
    const { token } = theme.useToken()
    const palette = useChartPalette()
    const { overview, isOverviewLoading } = dashboardManager
    const sources = overview?.sources
    const colors = { won: token.colorSuccess, priced: palette[0]!, new: palette[2]!, lost: token.colorError }
    const empty =
        sources &&
        sources.request.total +
            sources.tender.total +
            sources.offers.request +
            sources.offers.tender +
            sources.offers.application ===
            0

    return (
        <Panel
            block="overview-sources"
            title="Запити й тендери → заявки"
            subtitle={`Надійшли за ${periodCaption()}, за поточним станом`}
            loading={isOverviewLoading && !overview}
            empty={empty ? 'За період не надходило запитів і тендерів' : false}
            skeletonRows={8}
        >
            {sources ? (
                <>
                    <SourceRow title="Запити" funnel={sources.request} colors={colors} />
                    <SourceRow title="Тендери" funnel={sources.tender} colors={colors} />
                    <div className={classNames(styles['offers'])}>
                        {(
                            [
                                ['request', 'Пропозицій по запитах'],
                                ['tender', 'Пропозицій по тендерах'],
                                ['application', 'Пропозицій по заявках'],
                            ] as const
                        ).map(([key, title]) => (
                            <div key={key} data-offers={key}>
                                <div className={classNames(styles['chipValue'])}>{formatInt(sources.offers[key])}</div>
                                <div className={classNames(styles['chipLabel'])} title={title}>
                                    {title}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : null}
        </Panel>
    )
})
